import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  Button, IconView16, CircularLoader, IconClock16,
  IconCheckmark16, IconInfo16, colors
} from '@dhis2/ui'
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../auth/AuthContext';
import { useMyObjectivesContext } from './MyObjectivessPage'
import { usePerformanceContext } from '../../features/performance/PerformanceContext'
import { useCurrentUserPosition } from '../appraisal/hooks/useCurrentUserPosition';

// ✅ FULL ROLE MAPPING
const ROLE_NAMES = {
  'R3lFolCMdoi': 'Admin',
  'nLSSLch316I': 'CEO',
  'nLSSLch316l': 'CEO',
  'adeqQmxfCLF': 'HOD',
  'gtMJNK0QVL7': 'Top Staff',
  'vdVjNaqxblC': 'Senior Staff',
};

const getRoleName = (roleId) => {
  if (!roleId) return 'Not Assigned';
  return ROLE_NAMES[roleId] || roleId.substring(0, 8);
};

// Helper to check if date is within current fiscal year
const isWithinFiscalYear = (dateStr, fiscalYear) => {
  if (!dateStr || !fiscalYear?.start || !fiscalYear?.end) return false;

  try {
    return dateStr >= fiscalYear.start && dateStr <= fiscalYear.end;
  } catch (error) {
    console.warn('Date comparison error:', dateStr, error);
    return false;
  }
};

// ✅ Print styles for objectives list
const printStyles = `
  @media print {
    /* Force all objectives to be expanded in print */
    .my-objectives-list .objective-details {
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
      height: auto !important;
    }

    .my-objectives-list .objective-card {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      margin-bottom: 8px !important;
    }

    .my-objectives-list .view-details-btn {
      display: none !important;
    }

    /* Ensure all content is visible */
    .my-objectives-list .kra-list,
    .my-objectives-list .kpi-list,
    .my-objectives-list .alignment-info,
    .my-objectives-list .description-text {
      display: block !important;
      max-height: none !important;
      overflow: visible !important;
    }

    /* Compact print layout */
    .my-objectives-list .objective-header {
      padding: 8px 12px !important;
    }

    .my-objectives-list .objective-details {
      padding: 8px 12px !important;
    }

    /* Ensure KPAs and KPIs are fully visible */
    .my-objectives-list .kra-item,
    .my-objectives-list .kpi-item {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      margin-bottom: 4px !important;
    }

    /* Hide expand/collapse controls */
    .my-objectives-list .expand-control {
      display: none !important;
    }

    /* Status badges in print */
    .my-objectives-list .status-badge {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
    }

    /* Ensure background colors print */
    .my-objectives-list .kra-item,
    .my-objectives-list .kpi-item {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
    }

    /* ============================================ */
    /* ENSURE READABLE TEXT IN PRINT */
    /* ============================================ */
    
    /* Force minimum font sizes for readability */
    body, p, span, div, li, td, th {
      font-size: 12pt !important;
      line-height: 1.5 !important;
    }
    
    h1 { font-size: 18pt !important; }
    h2 { font-size: 16pt !important; }
    h3 { font-size: 14pt !important; }
    h4 { font-size: 13pt !important; }
    h5 { font-size: 12pt !important; }
    
    /* Ensure text is dark for readability */
    * {
      color: #000000 !important;
    }
    
    /* Make sure text is not too small */
    .apr-obj-name > span:first-child {
      font-size: 12pt !important;
    }
    
    .apr-staff-header > div:first-child > div:first-child {
      font-size: 11pt !important;
    }
    
    .report-shell__manual-rating-row .label {
      font-size: 11pt !important;
    }
    
    .report-shell__manual-rating-row .value {
      font-size: 11pt !important;
    }
    
    /* Better spacing for readability */
    .apr-obj-row {
      padding: 4px 8px !important;
    }
    
    .apr-obj-panel {
      padding: 4px 8px !important;
    }
    
    .objective-card {
      padding: 8px !important;
    }
  }
`;

const MyObjectivesList = ({
  isCEO = false,
  isHOD = false,
  isTopStaff = false,
  isSeniorStaff = false,
  reportMode = false,
  title = "My Objectives List",
  containerId = 'my-objectives-content'
}) => {
  const { user } = useAuth();
  const engine = useDataEngine();
  const { events = [], setLoading, loadingEvents } = useMyObjectivesContext()
  const [objectivesWithRoles, setObjectivesWithRoles] = useState([]);
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());
  const [localLoading, setLocalLoading] = useState(true);
  const userRoleCache = useRef(new Map())
  const objectiveNameCache = useRef(new Map())
  const goalCache = useRef(new Map());
  const positionNameCache = useRef(new Map())
  const loadingTimerRef = useRef(null);
  const containerRef = useRef(null);
  const hasLoadedRef = useRef(false); // ✅ Prevent duplicate loads
   
  const { fiscalYear } = usePerformanceContext();
  const { reportingToPosition } = useCurrentUserPosition(user?.id, user?.organisationUnits || []);
  const processedObjectiveIds = useRef(new Set());
  const [managerName, setManagerName] = useState('');
  const [managerTitle, setManagerTitle] = useState('');

  const normalizedLoggedInUser = useMemo(() => 
    user?.username?.toLowerCase().trim() || '',
    [user?.username]
  );

  // ✅ Filter: current user's objectives, with names, and within current FY
  const myObjectives = useMemo(() => {
    return objectivesWithRoles.filter(obj => {
      const objUsername = obj.username?.toLowerCase().trim() || '';
      const hasValidName = obj.name && obj.name.trim() !== '' && obj.name !== 'Unnamed';

      const startDate = obj.startDate || '';
      const completionDate = obj.completionDate || '';
      const isInCurrentFY = isWithinFiscalYear(startDate, fiscalYear) ||
        isWithinFiscalYear(completionDate, fiscalYear);

      return objUsername === normalizedLoggedInUser && hasValidName && isInCurrentFY;
    });
  }, [objectivesWithRoles, normalizedLoggedInUser, fiscalYear]);

  const objectiveIdsKey = useMemo(() => 
    myObjectives.map((objective) => objective.id).join('|'),
    [myObjectives]
  );

  const toggleExpand = (objectiveId) => {
    if (reportMode) return
    setExpandedObjectives(prev => {
      const newSet = new Set(prev)
      if (newSet.has(objectiveId)) newSet.delete(objectiveId)
      else newSet.add(objectiveId)
      return newSet
    })
  }

  const extractPositionAttributes = (attributes = []) => {
    const map = {};
    attributes.forEach((attr) => {
      map[attr.attribute] = attr.value;
    });
    return map;
  };

  const alignmentTypeCache = useRef(new Map());

const fetchAlignmentTypeName = useCallback(async (alignmentTypeId) => {
  if (!alignmentTypeId) return null;
  if (alignmentTypeCache.current.has(alignmentTypeId)) {
    return alignmentTypeCache.current.get(alignmentTypeId);
  }

  try {
    // Try trackedEntityInstances first (since it looks like a TEI ID)
    const response = await engine.query({
      alignment: {
        resource: `trackedEntityInstances/${alignmentTypeId}`,
        params: {
          fields: 'attributes[attribute,value],trackedEntityType[id,name]'
        }
      }
    });

    const attributes = response.alignment?.attributes || [];
    // Try common name attributes — adjust attribute IDs as needed
    const nameAttr = attributes.find(a => 
      a.attribute === 'w3bwqUZoc6V' || 
      a.attribute === 'NWe61Sa41CS' ||
      a.attribute === 'EuN5ztFnXsQ'
    );
    
    const name = nameAttr?.value || 
                 response.alignment?.trackedEntityType?.name || 
                 null;
    
    alignmentTypeCache.current.set(alignmentTypeId, name);
    return name;
  } catch (error) {
    // Fallback: try as an event
    try {
      const response = await engine.query({
        alignment: {
          resource: `events/${alignmentTypeId}`,
          params: {
            fields: 'dataValues[dataElement,value]'
          }
        }
      });
      const dataValues = response.alignment?.dataValues || [];
      const name = dataValues.find(dv => 
        dv.dataElement === 'EuN5ztFnXsQ' || 
        dv.dataElement === 'HJqqfzhH614'
      )?.value || null;
      
      alignmentTypeCache.current.set(alignmentTypeId, name);
      return name;
    } catch (fallbackError) {
      console.error('Error fetching alignment type name:', fallbackError);
      alignmentTypeCache.current.set(alignmentTypeId, null);
      return null;
    }
  }
}, [engine]);

  // Memoize fetch functions with useCallback
  const fetchPositionName = useCallback(async (positionTei) => {
    if (!positionTei) return null;
    if (positionNameCache.current.has(positionTei)) {
      return positionNameCache.current.get(positionTei);
    }

    try {
      const response = await engine.query({
        position: {
          resource: `trackedEntityInstances/${positionTei}`,
          params: {
            fields: 'attributes[attribute,value]'
          }
        }
      });

      const attributes = response.position?.attributes || [];
      const titleAttr = attributes.find(a => a.attribute === 'w3bwqUZoc6V' || a.attribute === 'NWe61Sa41CS');
      const name = titleAttr?.value || null;
      positionNameCache.current.set(positionTei, name);
      return name;
    } catch (error) {
      console.error('Error fetching position name:', error);
      positionNameCache.current.set(positionTei, null);
      return null;
    }
  }, [engine]);

  const fetchGoalDetails = useCallback(async (goalId) => {
    if (!goalId) return null;
    if (goalCache.current.has(goalId)) {
      return goalCache.current.get(goalId);
    }

    try {
      const response = await engine.query({
        goal: {
          resource: `events/${goalId}`,
          params: {
            fields: 'dataValues[dataElement,value]'
          }
        }
      });

      const dataValues = response.goal?.dataValues || [];
      
      const goalData = {
        name: dataValues.find(dv => dv.dataElement === 'HJqqfzhH614')?.value ||
              dataValues.find(dv => dv.dataElement === 'zS8QIgZc0Ll')?.value ||
              null,
        weight: parseInt(dataValues.find(dv => dv.dataElement === 'pOOGDRzEo98')?.value) || 0,
        priority: dataValues.find(dv => dv.dataElement === 'Co2i5BleCcZ')?.value || '',
        description: dataValues.find(dv => dv.dataElement === 'zS8QIgZc0Ll')?.value || '',
        progress: parseInt(dataValues.find(dv => dv.dataElement === 'pOOGDRzEo98')?.value) || 0,
        startDate: dataValues.find(dv => dv.dataElement === 'MH7DQVegD1j')?.value || '',
        endDate: dataValues.find(dv => dv.dataElement === 'pUvJQ9vy3A3')?.value || '',
      };
      
      goalCache.current.set(goalId, goalData);
      return goalData;
    } catch (error) {
      console.error('Error fetching goal details:', error);
      goalCache.current.set(goalId, null);
      return null;
    }
  }, [engine]);

  const fetchObjectiveName = useCallback(async (objectiveId) => {
    if (!objectiveId) return null;
    if (objectiveNameCache.current.has(objectiveId)) {
      return objectiveNameCache.current.get(objectiveId);
    }

    try {
      const response = await engine.query({
        objective: {
          resource: `events/${objectiveId}`,
          params: {
            fields: 'dataValues[dataElement,value]'
          }
        }
      });

      const dataValues = response.objective?.dataValues || [];
      const name = dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || null;
      objectiveNameCache.current.set(objectiveId, name);
      return name;
    } catch (error) {
      console.error('Error fetching objective name:', error);
      objectiveNameCache.current.set(objectiveId, null);
      return null;
    }
  }, [engine]);

  const fetchUserRole = useCallback(async (username) => {
    if (!username) return null
    if (userRoleCache.current.has(username)) {
      return userRoleCache.current.get(username)
    }

    try {
      const { users } = await engine.query({
        users: {
          resource: 'users',
          params: {
            filter: `username:eq:${username}`,
            fields: 'id,userCredentials[userRoles[id,name]]',
          },
        },
      })

      const role = users?.users?.[0]?.userCredentials?.userRoles?.[0]?.id || null
      userRoleCache.current.set(username, role)
      return role
    } catch (error) {
      console.error('Error fetching user role:', error)
      userRoleCache.current.set(username, null)
      return null
    }
  }, [engine]);

  const fetchLinkedEvents = useCallback(async (objectiveId, type) => {
    try {
      let programStage = type === 'kra' ? 'BJb3qTgbcTF' : 'ivyCLjNLFrd';

      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: programStage,
            filter: [`plJovUlxfQ0:eq:${objectiveId}`],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      });

      let events = [];
      if (response.events && Array.isArray(response.events.events)) {
        events = response.events.events;
      } else if (Array.isArray(response.events)) {
        events = response.events;
      }

      if (type === 'kra') {
        return events.map(event => ({
          id: event.event,
          name: event.dataValues?.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || '',
          description: event.dataValues?.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '',
          weight: parseInt(event.dataValues?.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value) || 0
        }));
      } else {
        return events.map(event => ({
          id: event.event,
          name: event.dataValues?.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || '',
          description: event.dataValues?.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '',
          target: event.dataValues?.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || ''
        }));
      }
    } catch (error) {
      console.error(`Error fetching linked ${type}s:`, error);
      return [];
    }
  }, [engine]);

  // ✅ Memoize transformEventToObjective with useCallback
  const transformEventToObjective = useCallback(async (event) => {
    try {
      // Filter out task, KRA, KPI events
      const isTaskEvent = event.programStage === 'Nv9nYNcBTdM';
      const isKraEvent = event.programStage === 'BJb3qTgbcTF';
      const isKpiEvent = event.programStage === 'ivyCLjNLFrd';

      if (isTaskEvent || isKraEvent || isKpiEvent) {
        return null;
      }

      const usernameRaw = event.storedBy ||
        event.createdByUserInfo?.username ||
        event.lastUpdatedByUserInfo?.username ||
        null;

      if (!usernameRaw) return null;

      const username = usernameRaw.toLowerCase().trim();

      if (username !== normalizedLoggedInUser) {
        return null;
      }

      const userRole = await fetchUserRole(username);

      const positionTei = event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value || '';

      let positionName = '';
      let isTopLevelUser = false;

      if (positionTei) {
        positionName = await fetchPositionName(positionTei) || '';
        
        try {
          const positionResponse = await engine.query({
            position: {
              resource: `trackedEntityInstances/${positionTei}`,
              params: {
                fields: 'attributes[attribute,value]'
              }
            }
          });
          const attrs = extractPositionAttributes(positionResponse.position?.attributes || []);
          isTopLevelUser = !attrs['SjP47qi3JeB'];
        } catch (error) {
          console.warn('Could not check top-level status for position:', positionTei);
        }
      }

      const kras = await fetchLinkedEvents(event.event, 'kra');
      const kpis = await fetchLinkedEvents(event.event, 'kpi');

      const name = event.dataValues?.find((dv) => dv.dataElement === 'EuN5ztFnXsQ')?.value || '';

      if (!name || name.trim() === '') {
        return null;
      }

      const higherLevelId = event.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
      let higherLevelName = '';
      if (higherLevelId) {
        higherLevelName = await fetchObjectiveName(higherLevelId) || higherLevelId;
      }

      const companyGoalId = event.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value || '';
      let companyGoalName = '';
      let companyGoalWeight = 0;
      let companyGoalPriority = '';

      if (companyGoalId) {
        const goalData = await fetchGoalDetails(companyGoalId);
        if (goalData) {
          companyGoalName = goalData.name || companyGoalId;
          companyGoalWeight = goalData.weight || 0;
          companyGoalPriority = goalData.priority || '';
        }
      }

      const startDate = event.dataValues?.find((dv) => dv.dataElement === 'eDcVy661j9e')?.value || '';
      const completionDate = event.dataValues?.find((dv) => dv.dataElement === 'J0rjxrDKn1l')?.value || '';

      const isInCurrentFY = isWithinFiscalYear(startDate, fiscalYear) ||
        isWithinFiscalYear(completionDate, fiscalYear);

      if (!isInCurrentFY) {
        return null;
      }

      const storedApprovalStatus = event.dataValues?.find(dv => dv.dataElement === 'DzQorireRBf')?.value || 'pending';
      const approvalStatus = isTopLevelUser ? 'approved' : storedApprovalStatus;

      const objectiveAlignmentId = event.dataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value || '';
        let objectiveAlignmentName = '';
        if (objectiveAlignmentId) {
          objectiveAlignmentName = await fetchAlignmentTypeName(objectiveAlignmentId) || objectiveAlignmentId;
       }

      return {
        id: event.event,
        name: name,
        description: event.dataValues?.find((dv) => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
        orgUnit: event.orgUnit,
        startDate: startDate,
        completionDate: completionDate,
        username,
        userRole,
        roleName: getRoleName(userRole),
        positionName: positionName,
        approvalStatus: approvalStatus,
        approvalRemark: event.dataValues?.find(dv => dv.dataElement === 'KeGWxm1IDYF')?.value || '',
        approvedBy: event.dataValues?.find(dv => dv.dataElement === 'owgP6wkRUwU')?.value || '',
        objectiveAlignment: event.dataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value || '',
        objectiveRoleAssigned: event.dataValues?.find(dv => dv.dataElement === 'vFoMLF5eZ5X')?.value || '',
        higherLevelObjectiveId: higherLevelId,
        higherLevelObjectiveName: higherLevelName,
        companyGoalId: companyGoalId,
        objectiveAlignment: objectiveAlignmentId,
        objectiveAlignmentName: objectiveAlignmentName,
        companyGoalName: companyGoalName,
        companyGoalWeight: companyGoalWeight,      
        companyGoalPriority: companyGoalPriority,
        kras: kras,
        kpis: kpis,
      }
    } catch (error) {
      console.error('Error transforming event:', error);
      return null;
    }
  }, [
    normalizedLoggedInUser, 
    fetchUserRole, 
    fetchPositionName, 
    fetchObjectiveName, 
    fetchGoalDetails, 
    fetchLinkedEvents, 
    fiscalYear, 
    engine
  ]);

  // ✅ Main load objectives function with useCallback
  const loadObjectives = useCallback(async () => {
    // Prevent duplicate loads
    if (hasLoadedRef.current && !reportMode) {
      console.log('⏭️ Objectives already loaded, skipping...');
      return;
    }

    if (!events.length || !user?.username) {
      setObjectivesWithRoles([]);
      setLoading(false);
      setLocalLoading(false);
      return;
    }

    setLoading(true);
    setLocalLoading(true);

    try {
      const transformed = await Promise.all(events.map(transformEventToObjective));

      const uniqueTransformed = [];
      const seenIds = new Set();
      transformed.forEach(obj => {
        if (obj && obj.id && !seenIds.has(obj.id)) {
          seenIds.add(obj.id);
          uniqueTransformed.push(obj);
        }
      });

      const filtered = uniqueTransformed.filter(obj =>
        obj !== null &&
        obj.username === normalizedLoggedInUser &&
        obj.name && obj.name.trim() !== ''
      );
      
      setObjectivesWithRoles(filtered);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading objectives:', error);
    } finally {
      setLoading(false);
      setLocalLoading(false);
    }
  }, [events, user?.username, normalizedLoggedInUser, transformEventToObjective, setLoading, reportMode]);

  // ✅ Single useEffect for loading objectives
  useEffect(() => {
    // Reset load flag when events change
    if (events.length > 0) {
      hasLoadedRef.current = false;
    }
    loadObjectives();
  }, [loadObjectives, events]);

  // ✅ Report mode notification
  useEffect(() => {
    if (containerRef.current && reportMode) {
      const event = new CustomEvent('objectivesLoaded', {
        detail: { container: containerRef.current }
      });
      document.dispatchEvent(event);
    }
  }, [reportMode, myObjectives.length]);

  // ✅ In report mode or print, expand ALL objectives by default
  useEffect(() => {
    if ((reportMode || expandedObjectives.size === 0) && myObjectives.length > 0) {
      const allIds = new Set(myObjectives.map(obj => obj.id));
      setExpandedObjectives(allIds);
    }
  }, [reportMode, objectiveIdsKey]);

  // ✅ Fetch manager info - memoized with useCallback
  const fetchManagerInfo = useCallback(async () => {
    if (!reportingToPosition?.trackedEntityInstance) {
      setManagerName('');
      setManagerTitle('');
      return;
    }

    try {
      const positionResponse = await engine.query({
        position: {
          resource: `trackedEntityInstances/${reportingToPosition.trackedEntityInstance}`,
          params: {
            fields: 'attributes[attribute,value]'
          }
        }
      });

      const attrs = extractPositionAttributes(positionResponse.position?.attributes || []);
      const title = attrs['w3bwqUZoc6V'] || attrs['NWe61Sa41CS'] || 'Manager';
      setManagerTitle(title);

      const incumbentId = attrs['V66IFsw5kq6'];
      if (incumbentId) {
        try {
          const userResponse = await engine.query({
            user: {
              resource: `users/${incumbentId}`,
              params: {
                fields: 'displayName,name,username'
              }
            }
          });
          const name = userResponse.user?.displayName || 
                       userResponse.user?.name || 
                       userResponse.user?.username || 
                       'Manager';
          setManagerName(name);
        } catch (err) {
          console.warn('⚠️ Could not fetch user:', err);
          setManagerName('Manager');
        }
      } else {
        setManagerName('Manager');
      }
    } catch (error) {
      console.error('❌ Error fetching manager info:', error);
      setManagerName('Manager');
      setManagerTitle('Manager');
    }
  }, [reportingToPosition, engine]);

  // ✅ Single useEffect for manager info
  useEffect(() => {
    fetchManagerInfo();
  }, [fetchManagerInfo]);

  const isLoading = loadingEvents || localLoading;

  const getStatusInfo = (status) => {
    const map = {
      'approved': { color: '#28a745', bg: '#d4edda', label: 'Approved' },
      'pending': { color: '#ffc107', bg: '#fff3cd', label: 'Pending' },
      'returned': { color: '#dc3545', bg: '#f8d7da', label: 'Returned' },
      'draft': { color: '#6c757d', bg: '#e9ecef', label: 'Draft' },
    };
    return map[status?.toLowerCase()] || map['draft'];
  };

  // ✅ Get user info once for the header
  const userDisplayName = user?.displayName || user?.name || user?.username || 'User';
  const userPosition = myObjectives.length > 0 ? myObjectives[0]?.positionName || '' : '';
  const userRoleName = myObjectives.length > 0 ? myObjectives[0]?.roleName || '' : '';

  return (
    <>
      <style>{printStyles}</style>
      
      <div 
        className="my-objectives-list" 
        ref={containerRef}
        id={containerId}
        style={{
          minHeight: '100%',
          backgroundColor: reportMode ? 'transparent' : colors.grey50,
          position: 'relative',
          padding: reportMode ? '0' : '1.5rem',
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible'
        }}
      >
        {/* Loader */}
        {isLoading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem',
            gap: '16px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: `1px solid ${colors.grey200}`,
            maxWidth: '100%',
            margin: reportMode ? '0' : '0 auto',
            minHeight: '200px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <CircularLoader small />
            <div style={{
              color: colors.grey700,
              fontSize: '16px',
              fontWeight: '500',
            }}>
              Loading your objectives...
            </div>
            <div style={{
              color: colors.grey500,
              fontSize: '13px',
            }}>
              Please wait while we load your performance data
            </div>
          </div>
        )}

        {/* My Objectives Section */}
        {!isLoading && myObjectives.length > 0 && (
          <section style={{
            maxWidth: '100%',
            margin: reportMode ? '0' : '0 auto',
          }}>
            {/* Header Card */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              borderBottom: '3px solid #0f5132',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative accent */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-10%',
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle, rgba(15,81,50,0.06) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                position: 'relative',
                zIndex: 1
              }}>
                {/* Staff name and status */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#0a2a1a',
                    letterSpacing: '-0.01em'
                  }}>
                    {userDisplayName}
                  </span>
                  
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 12px',
                    backgroundColor: '#0f5132',
                    color: '#fff',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em'
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#4ade80',
                      display: 'inline-block',
                      animation: 'pulse 2s infinite'
                    }} />
                    Active
                  </div>
                  
                  <div style={{
                    padding: '3px 12px',
                    background: 'rgba(15,81,50,0.1)',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    color: '#0f5132',
                    fontWeight: 600,
                    border: '1px solid rgba(15,81,50,0.15)'
                  }}>
                    {reportMode ? 'Report View' : 'My Objectives'}
                  </div>
                </div>
                
                {/* Info chips */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  fontSize: '0.875rem',
                  color: '#4a5568'
                }}>
                  {userPosition && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#f8fafc',
                      padding: '4px 14px',
                      borderRadius: '20px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                    }}>
                      <span style={{ fontWeight: 600, color: '#1a3a5c' }}>Position:</span>
                      <span style={{ color: '#2d3748' }}>{userPosition}</span>
                    </div>
                  )}
                  
                  {managerName && managerTitle && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#f8fafc',
                      padding: '4px 14px',
                      borderRadius: '20px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                    }}>
                      <span style={{ fontWeight: 600, color: '#1a3a5c' }}>Reporting To:</span>
                      <span style={{ color: '#2d3748' }}>{managerName} ({managerTitle})</span>
                    </div>
                  )}
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#f8fafc',
                    padding: '4px 14px',
                    borderRadius: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <span style={{ fontWeight: 600, color: '#1a3a5c' }}>FY:</span>
                    <span style={{ color: '#2d3748' }}>
                      {fiscalYear?.start && fiscalYear?.end 
                        ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
                        : 'Not Set'}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#f8fafc',
                    padding: '4px 14px',
                    borderRadius: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <span style={{ fontWeight: 600, color: '#1a3a5c' }}>Objectives:</span>
                    <span style={{
                      fontWeight: 700,
                      color: '#0f5132',
                      background: '#e8f5e9',
                      padding: '0 8px',
                      borderRadius: '12px'
                    }}>
                      {myObjectives.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ Objectives List */}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {myObjectives.map((objective) => {
                const isExpanded = expandedObjectives.has(objective.id);
                const statusInfo = getStatusInfo(objective.approvalStatus);

                return (
                  <div
                    key={objective.id}
                    className="objective-card"
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      boxShadow: reportMode 
                          ? '0 1px 3px rgba(0,0,0,0.1)' 
                          : '0 1px 3px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.08)',
                      border: `1px solid ${reportMode ? colors.grey200 : colors.grey300}`,
                      overflow: 'hidden',
                      transition: "all 0.2s ease",
                    }}
                  >
                    {/* Objective Header */}
                    <div className="objective-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px',
                      padding: '1.25rem 1.5rem',
                    }}>
                      <div style={{ flex: 1, minWidth: '240px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap',
                          marginBottom: '6px',
                        }}>
                          <strong style={{
                            color: colors.grey900,
                            fontSize: '17px',
                            fontWeight: 600,
                          }}>
                            {objective.name}
                          </strong>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '16px',
                          flexWrap: 'wrap',
                          fontSize: '13px',
                          color: colors.grey600,
                          marginTop: '4px',
                          paddingLeft: '44px',
                        }}>
                          {objective.higherLevelObjectiveName && (
                            <span>📌 {objective.higherLevelObjectiveName}</span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: 'center' }}>
                        <span className="status-badge" style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          backgroundColor: statusInfo.bg,
                          color: statusInfo.color,
                          fontSize: '12px',
                          fontWeight: '600',
                          border: `1px solid ${statusInfo.color}30`,
                        }}>
                          {statusInfo.label}
                        </span>
                        {!reportMode && (
                          <Button
                            className="view-details-btn expand-control"
                            small
                            icon={<IconView16 />}
                            onClick={() => toggleExpand(objective.id)}
                            data-print-hidden="true"
                            style={{
                              backgroundColor: colors.grey50,
                              border: `1px solid ${colors.grey300}`,
                              borderRadius: '8px',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              {isExpanded ? "Hide" : "View"} Details
                            </span>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {(isExpanded || reportMode) && (
                      <div className="objective-details" style={{
                        padding: '1.5rem',
                        backgroundColor: colors.grey25 ?? '#f8fafc',
                        borderTop: `1px solid ${colors.grey200}`,
                      }}>
                        {/* Alignment */}
                        <div className="alignment-info" style={{ marginBottom: '18px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                          }}>
                            <span style={{ fontSize: '14px' }}>🔗</span>
                            <strong style={{
                              color: colors.grey700,
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              Alignment
                            </strong>
                          </div>
                          {(objective.companyGoalName || objective.higherLevelObjectiveName) ? (
                            <div style={{
                              fontSize: '14px',
                              color: colors.grey800,
                              padding: '12px 16px',
                              backgroundColor: colors.blue050,
                              borderRadius: '8px',
                              border: `1px solid ${colors.blue200}`,
                            }}>
                              {objective.companyGoalName && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span>🎯</span>
                                  <strong>Company Goal:</strong> {objective.companyGoalName}
                                  {objective.companyGoalWeight > 0 && (
                                    <span style={{
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: colors.green600,
                                      backgroundColor: colors.green100,
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      marginLeft: '8px',
                                    }}>
                                      Progress: {objective.companyGoalWeight}%
                                    </span>
                                  )}
                                  {objective.companyGoalPriority && (
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      color: colors.orange600,
                                      backgroundColor: colors.orange100,
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      marginLeft: '4px',
                                    }}>
                                      Priority: {objective.companyGoalPriority}
                                    </span>
                                  )}
                                </div>
                              )}
                              {objective.higherLevelObjectiveName && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span>📌</span>
                                  <strong>Manager Objective:</strong> {objective.higherLevelObjectiveName}
                                </div>
                              )}
                              {objective.objectiveAlignmentName && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span>🔗</span>
                                  <strong>Alignment Type:</strong> {objective.objectiveAlignmentName}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p style={{
                              fontSize: '14px',
                              color: colors.grey500,
                              fontStyle: 'italic',
                              margin: 0,
                              padding: '8px 12px',
                            }}>
                              No alignment set
                            </p>
                          )}
                        </div>

                        {/* Description */}
                        <div className="description-text" style={{ marginBottom: '18px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                          }}>
                            <span style={{ fontSize: '14px' }}>📋</span>
                            <strong style={{
                              color: colors.grey700,
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              Description
                            </strong>
                          </div>
                          <p style={{
                            margin: 0,
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: colors.grey800,
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                          }}>
                            {objective.description || 'No description provided'}
                          </p>
                        </div>

                        {/* Approval Remark */}
                        {objective.approvalRemark && (
                          <div style={{
                            padding: '14px 18px',
                            backgroundColor: '#fff8f1',
                            borderRadius: '8px',
                            border: `1px solid ${colors.orange200}`,
                            borderLeft: `4px solid ${colors.orange500}`,
                            marginBottom: '18px',
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '6px',
                            }}>
                              <span style={{ fontSize: '14px' }}>💬</span>
                              <strong style={{
                                color: colors.orange800,
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}>
                                Approval Remark
                              </strong>
                            </div>
                            <p style={{
                              margin: 0,
                              fontSize: '14px',
                              color: colors.orange800,
                              wordWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}>
                              {objective.approvalRemark}
                            </p>
                          </div>
                        )}

                        {/* KPAs */}
                        {objective.kras?.length > 0 && (
                          <div className="kra-list" style={{ marginBottom: '18px' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px',
                            }}>
                              <span style={{ fontSize: '14px' }}>📌</span>
                              <strong style={{
                                color: colors.grey700,
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}>
                                Key Performance Areas <span style={{ color: colors.grey500 }}>(Optional)</span> ({objective.kras.length})
                              </strong>
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                            }}>
                              {objective.kras.map((kra, idx) => (
                                <div key={idx} className="kra-item" style={{
                                  padding: '14px 18px',
                                  backgroundColor: '#eff4ff',
                                  borderRadius: '8px',
                                  border: `1px solid ${colors.blue200}`,
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '4px',
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: `${colors.blue600}20`,
                                        color: colors.blue600,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                      }}>
                                        {idx + 1}
                                      </span>
                                      <strong style={{ fontSize: '15px', color: colors.blue800 }}>
                                        {kra.name}
                                      </strong>
                                    </div>
                                    {kra.weight > 0 && (
                                      <span style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: colors.blue600,
                                        backgroundColor: colors.blue100,
                                        padding: '3px 10px',
                                        borderRadius: '12px',
                                        border: `1px solid ${colors.blue300}`,
                                      }}>
                                        {kra.weight}% weight
                                      </span>
                                    )}
                                  </div>
                                  {kra.description && (
                                    <div style={{
                                      fontSize: '13px',
                                      color: colors.grey600,
                                      marginTop: '6px',
                                      wordWrap: 'break-word',
                                      whiteSpace: 'pre-wrap',
                                      paddingLeft: '32px',
                                    }}>
                                      {kra.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* KPIs */}
                        <div className="kpi-list" style={{ marginBottom: '18px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                          }}>
                            <span style={{ fontSize: '14px' }}>📊</span>
                            <strong style={{
                              color: colors.grey700,
                              fontSize: '13px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              Key Performance Indicators <span style={{ color: colors.red500 }}>*</span> ({objective.kpis?.length || 0})
                            </strong>
                          </div>
                          {objective.kpis?.length > 0 ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                            }}>
                              {objective.kpis.map((kpi, idx) => (
                                <div key={idx} className="kpi-item" style={{
                                  padding: '12px 16px',
                                  backgroundColor: colors.green050,
                                  borderRadius: '8px',
                                  border: `1px solid ${colors.green200}`,
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                  }}>
                                    <strong style={{ fontSize: '14px', color: colors.green800 }}>
                                      {kpi.name}
                                    </strong>
                                    {kpi.target && (
                                      <span style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: colors.green700,
                                        backgroundColor: colors.green100,
                                        padding: '2px 10px',
                                        borderRadius: '12px',
                                      }}>
                                        Measure: {kpi.target}
                                      </span>
                                    )}
                                  </div>
                                  {kpi.description && (
                                    <div style={{ fontSize: '12px', color: colors.grey600, marginTop: '4px' }}>
                                      {kpi.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{
                              padding: '12px 16px',
                              backgroundColor: colors.grey100,
                              borderRadius: '8px',
                              fontSize: '14px',
                              color: colors.grey500,
                              fontStyle: 'italic',
                            }}>
                              No KPIs defined
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Empty state */}
        {!isLoading && myObjectives.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: `1px dashed ${colors.grey300}`,
            maxWidth: '500px',
            margin: '0 auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              opacity: 0.5,
            }}>
              📋
            </div>
            <p style={{
              fontSize: '16px',
              color: colors.grey700,
              margin: '0 0 8px 0',
              fontWeight: 500,
            }}>
              No objectives found for the current fiscal year.
            </p>
            <p style={{
              fontSize: '13px',
              color: colors.grey600,
              margin: 0,
            }}>
              Check back later or adjust your filters.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

export default MyObjectivesList;